import { t } from '@lingui/macro'
import { FC, useCallback, useState } from 'react'
import { InfiniteScrollPlate } from '@/ui/atoms/InfiniteScrollPlate'
import { ButtonVariant, InteractiveModal, InteractiveModalParts } from '@/ui/kit'
import { Loader } from '@/ui/kit/Loader'
import { EInteractiveModalVariant } from '@/ui/kit/Modals/InteractiveModal/InteractiveModal'
import { TSheetItem } from '@/ui/kit/Modals/InteractiveModal/InteractiveModalParts'
import { EmptyList } from '@/ui/molecules'
import { Table } from '@/ui/molecules/Table'
import { formatHistoryItemForModalOutput } from './HistoryTrade.utils/historyItemToSheetItem'
import styles from './style.module.scss'
import { isExchangeBinance, isExchangeBybit, isExchangeTigerX } from '@/utils/lib/exchange'
import { handleMouseupWithoutSelection } from '@/utils/lib/handleMouseupWithoutSelection'
import { getHintHistoryTigerX } from '@/ui/organisms/HistoryTrades/helpers/getHintHistoryTigerX'
import { useActiveExchage } from '@/utils/hooks/useActiveExchange'
import { HistoryTigerXFilter } from './filters/HistoryTigerXFilter'
import { useHistoryTradeData } from './HistoryTrade.hooks/useHistoryTradeData'
import { HistoryBybitFilter } from './filters/HistoryBybitFilter'
import { HistoryBinanceFilter } from './filters/HistoryBinanceFilter'

export const HistoryTrade: FC = () => {
  const { activeExchange } = useActiveExchage()
  const { historyTradeData, tableColumns, setFilters, loading, reachedEnd, loadMore, initialFilters, error } =
    useHistoryTradeData()

  const [currentBill, setCurrentBill] = useState<TSheetItem[]>()

  const handleModalOpen = useCallback(() => {
    if (handleMouseupWithoutSelection()) {
      return currentBill !== undefined
    }
  }, [currentBill])

  const rowHandler = useCallback(
    (bill: Record<string, string>) => {
      if (isExchangeBinance(activeExchange.type)) return
      setCurrentBill(formatHistoryItemForModalOutput(bill, activeExchange.type))
    },
    [activeExchange.type]
  )

  const clearBill = useCallback(() => {
    setCurrentBill(undefined)
  }, [])

  const scrollCallback = useCallback<IntersectionObserverCallback>(
    ([entry]) => {
      if (entry.isIntersecting && !loading && !reachedEnd) {
        loadMore()
      }
    },
    [loadMore, loading, reachedEnd]
  )

  console.log('currentBill', currentBill)

  return (
    <>
      {isExchangeBinance(activeExchange.type) && (
        <HistoryBinanceFilter initialFilters={initialFilters} onChange={setFilters} />
      )}
      {isExchangeBybit(activeExchange.type) && (
        <HistoryBybitFilter initialFilters={initialFilters} onChange={setFilters} />
      )}
      {isExchangeTigerX(activeExchange.type) && (
        <HistoryTigerXFilter initialFilters={initialFilters} onChange={setFilters} />
      )}

      <div className={styles.root}>
        {loading && historyTradeData && historyTradeData.length === 0 && <Loader className={styles.loader} />}

        {!loading && historyTradeData && historyTradeData.length === 0 && (
          <EmptyList
            label={t({
              id: 'historyTradePage.empty',
              comment: 'Exchange history will display here',
            })}
          />
        )}

        {historyTradeData && historyTradeData.length > 0 && (
          <div>
            <Table
              data={historyTradeData}
              accessors={tableColumns}
              rowCallback={rowHandler}
              rowHoverClassname={styles.rowHover}
              rowClassname={styles.row}
              getTigerXHistoryHint={isExchangeTigerX(activeExchange.type) ? getHintHistoryTigerX : undefined}
              isHistoryHintTigerX={isExchangeTigerX(activeExchange.type)}
            />
            {loading && <Loader className={styles.loader} />}
            <InfiniteScrollPlate className={styles.scrollIndicator} scrollCallback={scrollCallback} />
          </div>
        )}
      </div>

      <InteractiveModal isOpen={handleModalOpen()} variant={EInteractiveModalVariant.WIDE}>
        {currentBill && <InteractiveModalParts.Sheet source={currentBill} />}
        <InteractiveModalParts.Button
          text={t({ id: 'core.close', message: `Close` })}
          variant={ButtonVariant.Primary}
          onClick={clearBill}
        />
      </InteractiveModal>
    </>
  )
}
